import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Image,
  useToast,
  Spinner,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Divider,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { FaSearch, FaMagic } from 'react-icons/fa';
import {
  searchScryfall,
  createScambio,
  fetchScryfallSets,
  fetchCardSets,
} from '../../utils/scambiApi';

export default function AddCardModal({ isOpen, onClose, onCardAdded }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [availableSets, setAvailableSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(false);
  const [cardSpecificSets, setCardSpecificSets] = useState([]);
  const [loadingCardSets, setLoadingCardSets] = useState(false);
  const [selectedSetImage, setSelectedSetImage] = useState(null);

  const [formData, setFormData] = useState({
    tipo: 'cerco',
    note: '',
    lingua: 'it',
    set_name: '',
    set_code: '',
    collector_number: null,
    foil: false,
    condizioni: 'NM',
  });

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  const loadAvailableSets = async () => {
    setLoadingSets(true);
    try {
      const sets = await fetchScryfallSets();
      setAvailableSets(sets);
    } catch (error) {
      toast({
        title: 'Errore nel caricamento dei set',
        description: 'Impossibile caricare i set disponibili',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingSets(false);
    }
  };

  const loadCardSpecificSets = async (cardName) => {
    if (!cardName || cardName.trim().length < 2) {
      setCardSpecificSets([]);
      return;
    }

    setLoadingCardSets(true);
    try {
      const data = await fetchCardSets(cardName);
      setCardSpecificSets(data.sets || []);

      if (data.sets && data.sets.length > 0) {
        toast({
          title: 'Set trovati',
          description: `Trovati ${data.sets.length} set per "${cardName}"`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Errore nel caricamento dei set',
        description: 'Impossibile caricare i set specifici per questa carta',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      setCardSpecificSets([]);
    } finally {
      setLoadingCardSets(false);
    }
  };

  const handleSetChange = (setName) => {
    setFormData((prev) => ({ ...prev, set_name: setName }));

    // Trova il set selezionato e aggiorna l'immagine
    if (setName && cardSpecificSets.length > 0) {
      const selectedSet = cardSpecificSets.find((set) => set.name === setName);
      if (selectedSet && selectedSet.prints.length > 0) {
        // Usa la prima stampa del set per l'immagine
        const firstPrint = selectedSet.prints[0];
        setSelectedSetImage(
          firstPrint.image_uris?.normal || firstPrint.image_uris?.small
        );

        // Aggiorna anche il set code per la creazione dello scambio
        setFormData((prev) => ({
          ...prev,
          set_code: selectedSet.code,
          collector_number: firstPrint.collector_number || null,
        }));

        toast({
          title: 'Set cambiato',
          description: `Immagine aggiornata per ${selectedSet.name}`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } else if (!setName) {
      // Se non è selezionato nessun set, usa l'immagine originale della carta
      setSelectedSetImage(
        selectedCard?.image_uris?.normal || selectedCard?.image_uris?.small
      );

      // Rimuovi set code e collector number
      setFormData((prev) => ({
        ...prev,
        set_code: '',
        collector_number: null,
      }));
    }
  };

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadAvailableSets();
    }
  }, [isOpen]);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchScryfall(query);
        setSearchResults(results);
      } catch (error) {
        toast({
          title: 'Errore nella ricerca',
          description: 'Impossibile cercare le carte',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    setSearchResults([]);
    if (searchInputRef.current) {
      searchInputRef.current.value = card.name;
    }

    // Carica i set specifici per questa carta
    loadCardSpecificSets(card.name);

    // Imposta automaticamente il set_name se disponibile
    if (card.set_name) {
      setFormData((prev) => ({ ...prev, set_name: card.set_name }));
    }

    // Imposta l'immagine iniziale della carta
    setSelectedSetImage(card.image_uris?.normal || card.image_uris?.small);
  };

  const resetForm = () => {
    setFormData({
      tipo: 'cerco',
      note: '',
      lingua: 'it',
      set_name: '',
      set_code: '',
      collector_number: null,
      foil: false,
      condizioni: 'NM',
    });
    setSelectedCard(null);
    setSearchResults([]);
    setCardSpecificSets([]);
    setSelectedSetImage(null);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedCard) {
      toast({
        title: 'Carta richiesta',
        description: 'Seleziona una carta dalla ricerca',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await createScambio({
        nome: selectedCard.name,
        cmc: selectedCard.cmc,
        uri: selectedCard.uri,
        fonte: 'scryfall',
        power: selectedCard.power,
        colors: selectedCard.colors,
        lingua: formData.lingua,
        prices: selectedCard.prices,
        rarity: selectedCard.rarity,
        set_name: formData.set_name || selectedCard.set_name,
        set: formData.set_code || selectedCard.set,
        collector_number:
          formData.collector_number || selectedCard.collector_number,
        mana_cost: selectedCard.mana_cost,
        toughness: selectedCard.toughness,
        type_line: selectedCard.type_line,
        condizioni: formData.condizioni,
        foil: formData.foil,
        image_uris: selectedSetImage
          ? {
              normal: selectedSetImage,
              small: selectedSetImage,
              large: selectedSetImage,
            }
          : selectedCard.image_uris,
        legalities: selectedCard.legalities,
        scryfallId: selectedCard.id,
        oracle_text: selectedCard.oracle_text,
        scryfall_uri: selectedCard.scryfall_uri,
        color_identity: selectedCard.color_identity,

        note: formData.note || '',
        tipo: formData.tipo, // Usa il valore selezionato dall'utente
      });

      onCardAdded();
      resetForm();
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile creare lo scambio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FaMagic} color="purple.500" />
            <Text>Aggiungi Carta Magic</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Search Section */}
            <Box>
              <FormLabel fontWeight="bold" color="gray.700">
                Cerca Carta
              </FormLabel>
              <InputGroup>
                <Input
                  ref={searchInputRef}
                  placeholder="Inizia a digitare il nome della carta..."
                  onChange={(e) => handleSearch(e.target.value)}
                  pr="4.5rem"
                />
                <InputRightElement width="4.5rem">
                  {searching ? (
                    <Spinner size="sm" />
                  ) : (
                    <Icon as={FaSearch} color="gray.400" />
                  )}
                </InputRightElement>
              </InputGroup>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Box
                  mt={3}
                  maxH="200px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg={cardBg}
                  boxShadow={cardShadow}
                >
                  {searchResults.map((card) => (
                    <Box
                      key={card.id}
                      p={3}
                      borderBottom="1px solid"
                      borderBottomColor="gray.100"
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => handleCardSelect(card)}
                    >
                      <HStack spacing={3}>
                        <Image
                          src={
                            card.image_uris?.small || '/default-card-image.png'
                          }
                          alt={card.name}
                          boxSize="40px"
                          objectFit="cover"
                          borderRadius="sm"
                          fallbackSrc="/default-card-image.png"
                        />
                        <Box flex={1}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {card.name}
                          </Text>
                          <HStack spacing={2} mt={1}>
                            <Badge size="sm" colorScheme="blue">
                              {card.set_name}
                            </Badge>
                            {card.rarity && (
                              <Badge size="sm" colorScheme="purple">
                                {card.rarity}
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Selected Card Preview */}
            {selectedCard && (
              <Box
                p={4}
                bg="blue.50"
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
              >
                <HStack spacing={4}>
                  <Image
                    src={
                      selectedSetImage ||
                      selectedCard.image_uris?.normal ||
                      selectedCard.image_uris?.small
                    }
                    alt={selectedCard.name}
                    boxSize="80px"
                    objectFit="cover"
                    borderRadius="md"
                    fallbackSrc="/default-card-image.png"
                  />
                  <Box flex={1}>
                    <Text fontWeight="bold" fontSize="lg" color="blue.800">
                      {selectedCard.name}
                    </Text>
                    <Text fontSize="sm" color="blue.600" mb={2}>
                      {selectedCard.set_name}
                    </Text>
                    <HStack spacing={2} wrap="wrap">
                      {selectedCard.rarity && (
                        <Badge size="sm" colorScheme="purple">
                          {selectedCard.rarity}
                        </Badge>
                      )}
                      {selectedCard.collector_number && (
                        <Badge size="sm" colorScheme="gray">
                          #{selectedCard.collector_number}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                </HStack>
              </Box>
            )}

            <Divider />

            {/* Form Fields */}
            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Tipo di Scambio
              </FormLabel>
              <Select
                value={formData.tipo}
                onChange={(e) =>
                  setFormData({ ...formData, tipo: e.target.value })
                }
              >
                <option value="cerco">Cerco questa carta</option>
                <option value="scambio">Scambio questa carta</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Lingua
              </FormLabel>
              <Select
                value={formData.lingua}
                onChange={(e) =>
                  setFormData({ ...formData, lingua: e.target.value })
                }
              >
                <option value="it">Italiano</option>
                <option value="en">Inglese</option>
                <option value="de">Tedesco</option>
                <option value="fr">Francese</option>
                <option value="es">Spagnolo</option>
                <option value="pt">Portoghese</option>
                <option value="ru">Russo</option>
                <option value="ja">Giapponese</option>
                <option value="ko">Coreano</option>
                <option value="zh">Cinese</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Set (opzionale)
              </FormLabel>
              <Select
                value={formData.set_name}
                onChange={(e) => handleSetChange(e.target.value)}
                placeholder="Seleziona un set..."
                isDisabled={loadingSets || loadingCardSets}
              >
                <option value="">Nessun set specifico</option>

                {/* Set specifici per la carta selezionata */}
                {cardSpecificSets.length > 0 && (
                  <optgroup
                    label={`Set disponibili per ${selectedCard?.name || 'questa carta'}`}
                  >
                    {cardSpecificSets.map((set) => (
                      <option key={set.code} value={set.name}>
                        {set.name} ({set.code.toUpperCase()}) -{' '}
                        {set.prints.length} versioni
                      </option>
                    ))}
                  </optgroup>
                )}

                {/* Fallback: mostra tutti i set solo se non ci sono set specifici */}
                {cardSpecificSets.length === 0 && availableSets.length > 0 && (
                  <optgroup label="Set generici disponibili">
                    {availableSets.slice(0, 50).map((set) => (
                      <option key={set.code} value={set.name}>
                        {set.name} ({set.code.toUpperCase()})
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>

              {loadingCardSets && (
                <Text fontSize="sm" color="blue.500" mt={1}>
                  🔍 Ricerca set per &ldquo;{selectedCard?.name}&rdquo;...
                </Text>
              )}

              {loadingSets && !loadingCardSets && (
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Caricamento set...
                </Text>
              )}

              {cardSpecificSets.length > 0 && (
                <Text fontSize="sm" color="green.600" mt={1}>
                  ✅ Trovati {cardSpecificSets.length} set per &ldquo;
                  {selectedCard?.name}&rdquo;
                </Text>
              )}

              {cardSpecificSets.length === 0 &&
                selectedCard &&
                !loadingCardSets && (
                  <Text fontSize="sm" color="orange.600" mt={1}>
                    ⚠️ Nessun set specifico trovato per &ldquo;
                    {selectedCard.name}&rdquo;. Usa i set generici disponibili.
                  </Text>
                )}
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Condizioni
              </FormLabel>
              <Select
                value={formData.condizioni}
                onChange={(e) =>
                  setFormData({ ...formData, condizioni: e.target.value })
                }
              >
                <option value="NM">Near Mint (NM)</option>
                <option value="LP">Lightly Played (LP)</option>
                <option value="MP">Moderately Played (MP)</option>
                <option value="HP">Heavily Played (HP)</option>
                <option value="DMG">Damaged (DMG)</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Foil
              </FormLabel>
              <Select
                value={formData.foil ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData({ ...formData, foil: e.target.value === 'true' })
                }
              >
                <option value="false">No</option>
                <option value="true">Sì</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Note (opzionale)
              </FormLabel>
              <Textarea
                placeholder="Aggiungi dettagli, condizioni, preferenze..."
                value={formData.note}
                onChange={(e) =>
                  setFormData({ ...formData, note: e.target.value })
                }
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleClose}>
              Annulla
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Salvando..."
              isDisabled={!selectedCard}
            >
              Salva Scambio
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
